import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconButton, Badge, Tooltip, Box, Typography } from '@mui/material';
import { FiBell, FiClock, FiMenu } from 'react-icons/fi';
import { TbBellRinging } from 'react-icons/tb';
import { MdChecklistRtl, MdDarkMode, MdLightMode } from 'react-icons/md';
import { useThemeMode } from '../../theme/ThemeModeContext';
import { useAuth } from '../../hooks/useAuth';
import { usePendingPayment } from '../modal/PendingPaymentContext';
import { useSaleDue } from '../modal/SaleDueContext';

const TITLES: Record<string, string> = {
    '/welcome': 'Início',
    '/users': 'Usuários',
    '/product': 'Produtos',
    '/client': 'Clientes',
    '/utilitarian': 'Utilitários',
    '/scheduling': 'Agendamentos',
    '/pending': 'Pendências',
    '/task': 'Tarefas',
    '/tools': 'Ativos',
    '/moviment': 'Movimentação',
    '/sale': 'Frente de Caixa',
    '/sale/list': 'Vendas Realizadas',
    '/workorder': 'Ordens de Serviço',
    '/historic': 'Histórico',
    '/ticket': 'Ticket',
    '/company': 'Empresa',
    '/subscriptions-config': 'Assinaturas',
    '/subscriptions': 'Mensalidades',
};

interface TopbarProps {
    onToggleSidebar: () => void;
    hasPending?: boolean;
    /** Tarefas que venceram sem serem concluídas. */
    overdueTasks?: number;
}

const actionBtnSx = {
    color: 'text.secondary',
    border: '1px solid var(--border)',
    borderRadius: '11px',
    width: 38,
    height: 38,
    transition: 'all .2s ease',
    '&:hover': {
        color: 'primary.main',
        borderColor: 'var(--accent)',
        bgcolor: 'transparent',
        transform: 'translateY(-1px)',
    },
} as const;

const Topbar: React.FC<TopbarProps> = ({ onToggleSidebar, hasPending = false, overdueTasks = 0 }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { mode, toggle } = useThemeMode();
    const { userProfile } = useAuth();
    const { pendings, hasOverdue, openModal } = usePendingPayment();
    const { dueSales, openModal: openSaleDueModal } = useSaleDue();

    const title = TITLES[location.pathname] ?? 'Painel';

    // Aviso fixo de mensalidade. Enquanto não vence, é só este sino — o modal
    // só interrompe o login quando a cobrança já passou do vencimento.
    // O texto fica no tooltip; na barra basta o sino vermelho.
    const pendingPaymentTooltip = hasOverdue
        ? pendings.length === 1 ? 'Mensalidade vencida' : `${pendings.length} mensalidades vencidas`
        : pendings.length === 1 ? 'Mensalidade a vencer' : `${pendings.length} mensalidades a vencer`;

    const handlePendingPaymentClick = () => {
        // Admin administra as cobranças de todas as empresas na tela própria;
        // o responsável pela empresa resolve a dele no modal.
        if (userProfile === 'ADMIN') navigate('/subscriptions');
        else openModal();
    };

    return (
        <Box
            sx={{
                height: 68,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 2.5,
                background: 'var(--surface-glass)',
                backdropFilter: 'blur(14px)',
                borderBottom: '1px solid var(--border)',
                position: 'sticky',
                top: 0,
                zIndex: 10,
            }}
        >
            <Tooltip title="Recolher / expandir menu">
                <IconButton onClick={onToggleSidebar} sx={actionBtnSx} size="small">
                    <FiMenu />
                </IconButton>
            </Tooltip>

            <Box sx={{ ml: 0.5, display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
                <Typography
                    component="span"
                    sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'text.disabled' }}
                >
                    Thunnar
                </Typography>
                <Typography
                    component="h1"
                    sx={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 21,
                        fontWeight: 700,
                        letterSpacing: '-0.02em',
                        background: 'var(--gradient-brand)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                    }}
                >
                    {title}
                </Typography>
            </Box>

            <Box sx={{ flex: 1 }} />

            {pendings.length > 0 && (
                <Tooltip title={`${pendingPaymentTooltip} — clique para ver`}>
                    <IconButton
                        onClick={handlePendingPaymentClick}
                        size="small"
                        sx={{
                            ...actionBtnSx,
                            color: '#ef4444',
                            borderColor: '#ef4444',
                            '&:hover': {
                                color: '#ef4444',
                                borderColor: '#ef4444',
                                bgcolor: 'rgba(239, 68, 68, 0.08)',
                                transform: 'translateY(-1px)',
                            },
                        }}
                    >
                        <Badge
                            color="error"
                            variant={pendings.length > 1 ? 'standard' : 'dot'}
                            badgeContent={pendings.length > 1 ? pendings.length : undefined}
                            overlap="circular"
                        >
                            <FiBell />
                        </Badge>
                    </IconButton>
                </Tooltip>
            )}

            {dueSales.length > 0 && (
                <Tooltip
                    title={
                        (dueSales.length === 1
                            ? 'Venda a prazo vencendo'
                            : `${dueSales.length} vendas a prazo vencendo`) + ' — clique para confirmar'
                    }
                >
                    <IconButton
                        onClick={() => openSaleDueModal()}
                        size="small"
                        sx={{
                            ...actionBtnSx,
                            color: '#f59e0b',
                            borderColor: '#f59e0b',
                            '&:hover': {
                                color: '#f59e0b',
                                borderColor: '#f59e0b',
                                bgcolor: 'rgba(245, 158, 11, 0.08)',
                                transform: 'translateY(-1px)',
                            },
                        }}
                    >
                        <Badge
                            color="warning"
                            variant={dueSales.length > 1 ? 'standard' : 'dot'}
                            badgeContent={dueSales.length > 1 ? dueSales.length : undefined}
                            overlap="circular"
                        >
                            <FiClock />
                        </Badge>
                    </IconButton>
                </Tooltip>
            )}

            <Tooltip title={mode === 'light' ? 'Modo escuro' : 'Modo claro'}>
                <IconButton onClick={toggle} sx={actionBtnSx} size="small">
                    {mode === 'light' ? <MdDarkMode /> : <MdLightMode />}
                </IconButton>
            </Tooltip>

            <Tooltip title="Pendências">
                <IconButton onClick={() => navigate('/pending')} sx={actionBtnSx} size="small">
                    <Badge color="error" variant="dot" invisible={!hasPending}>
                        <TbBellRinging />
                    </Badge>
                </IconButton>
            </Tooltip>

            {/* Só aparece quando há tarefa vencida sem conclusão; o clique abre
                direto a lista das não realizadas, onde dá para reabrir ou cancelar. */}
            {overdueTasks > 0 && (
                <Tooltip
                    title={
                        overdueTasks === 1
                            ? '1 tarefa não realizada — clique para ver'
                            : `${overdueTasks} tarefas não realizadas — clique para ver`
                    }
                >
                    <IconButton
                        onClick={() => navigate('/task?overdue=1')}
                        size="small"
                        sx={{
                            ...actionBtnSx,
                            color: '#ef4444',
                            borderColor: '#ef4444',
                            '&:hover': {
                                color: '#ef4444',
                                borderColor: '#ef4444',
                                bgcolor: 'rgba(239, 68, 68, 0.08)',
                                transform: 'translateY(-1px)',
                            },
                        }}
                    >
                        <Badge
                            color="error"
                            variant={overdueTasks > 1 ? 'standard' : 'dot'}
                            badgeContent={overdueTasks > 1 ? overdueTasks : undefined}
                            overlap="circular"
                        >
                            <MdChecklistRtl />
                        </Badge>
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

export default Topbar;
